import {ColorSchemeContext} from '@common/core/color-scheme-provider';
import {ButtonBase} from '@ui/buttons/button-base';
import {Trans} from '@ui/i18n/trans';
import {LucideIcon} from '@ui/icons/lucide/lucide-icon-wrapper';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import clsx from 'clsx';
import {CheckIcon, PaletteIcon} from 'lucide-react';
import {use} from 'react';

export function ThemeSelectorDialog() {
  const {close} = useDialogContext();
  const {selectableThemes, selectedTheme, setSelectedTheme} = use(
    ColorSchemeContext,
  );

  return (
    <Dialog size="md">
      <DialogHeader leftAdornment={<LucideIcon icon={PaletteIcon} size="sm" />}>
        <Trans message="Choose theme" />
      </DialogHeader>
      <DialogBody>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {selectableThemes.map(theme => {
            const isSelected = selectedTheme?.id === theme.id;
            return (
              <ButtonBase
                key={theme.id}
                display="block"
                className={clsx(
                  'relative rounded-card border p-3 text-left transition-colors hover:bg-accent',
                  isSelected && 'border-primary ring-2 ring-primary/30',
                )}
                onClick={() => {
                  setSelectedTheme(theme);
                  close();
                }}
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium text-sm">{theme.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {theme.is_dark ? (
                        <Trans message="Dark theme" />
                      ) : (
                        <Trans message="Light theme" />
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <LucideIcon icon={CheckIcon} size="xs" />
                    </span>
                  )}
                </div>
                <ThemeSwatches theme={theme} />
              </ButtonBase>
            );
          })}
        </div>
      </DialogBody>
    </Dialog>
  );
}

interface ThemeSwatchesProps {
  theme: {
    values: Record<string, string>;
  };
}
function ThemeSwatches({theme}: ThemeSwatchesProps) {
  const colors = [
    theme.values['--be-background'],
    theme.values['--be-card'],
    theme.values['--be-primary'],
    theme.values['--be-accent'],
    theme.values['--be-foreground'],
  ].filter(Boolean);

  return (
    <div className="flex h-8 overflow-hidden rounded-input border">
      {colors.map((color, index) => (
        <span
          key={`${color}-${index}`}
          className="flex-1"
          style={{background: color}}
        />
      ))}
    </div>
  );
}
